import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const IN_CHUNK = 30;

function displayNameFromUserData(data) {
  const firstName = data.firstName || '';
  const lastName = data.lastName || '';
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  return joined || String(data.email || '').trim();
}

function matchesPrimaryContact(name, email, primaryName, primaryEmail) {
  const pn = String(primaryName || '').trim().toLowerCase();
  const pe = String(primaryEmail || '').trim().toLowerCase();
  const n = String(name || '').trim().toLowerCase();
  const e = String(email || '').trim().toLowerCase();
  if (pe && e && e === pe) return true;
  if (pn && n && n === pn) return true;
  return false;
}

export async function fetchAdditionalTeamMemberNamesByAccountIds(primaryByAccountId) {
  const result = new Map();
  const ids = [...primaryByAccountId.keys()];
  ids.forEach((id) => result.set(id, []));

  const docSeenByAccount = new Map();

  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    const chunk = ids.slice(i, i + IN_CHUNK);
    if (chunk.length === 0) continue;

    const usersRef = collection(db, 'users');
    const [snapA, snapC] = await Promise.all([
      getDocs(query(usersRef, where('accountId', 'in', chunk))),
      getDocs(query(usersRef, where('clientId', 'in', chunk))),
    ]);

    const ingestDoc = (docSnap) => {
      const data = docSnap.data();
      const aid = data.accountId || data.clientId;
      if (!aid || !result.has(aid)) return;

      if (!docSeenByAccount.has(aid)) docSeenByAccount.set(aid, new Set());
      const seenIds = docSeenByAccount.get(aid);
      if (seenIds.has(docSnap.id)) return;

      const name = displayNameFromUserData(data);
      if (!name) return;

      const email = String(data.email || '').trim();
      const primary = primaryByAccountId.get(aid);
      if (matchesPrimaryContact(name, email, primary?.contactName, primary?.contactEmail)) {
        return;
      }

      seenIds.add(docSnap.id);
      result.get(aid).push(name);
    };

    snapA.docs.forEach(ingestDoc);
    snapC.docs.forEach(ingestDoc);
  }

  for (const [, names] of result) {
    names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  return result;
}

function primaryContactFields(account) {
  const contact = account.contact || {};
  const contactName =
    contact.name ||
    (contact.firstName && contact.lastName
      ? `${contact.firstName} ${contact.lastName}`
      : contact.firstName || contact.lastName || '');
  const contactEmail = String(contact.email || account.email || '').trim();
  return { contactName, contactEmail };
}

function portalPersonDisplayName(data) {
  const fromParts = displayNameFromUserData(data);
  if (fromParts) return fromParts;
  const dn = String(data.displayName || '').trim();
  if (dn) return dn;
  return String(data.email || '').trim();
}

/**
 * All portal users tied to these accounts via users.accountId or users.clientId
 * (same linkage as kickoff/roadmap “additional team” discovery).
 * Each entry includes isPrimary when they match the CRM primary contact (name/email).
 */
export async function fetchPortalUsersLinkedToAccounts(accounts) {
  const result = new Map();
  if (!accounts?.length) return result;

  const ids = accounts.map((a) => a.id);
  ids.forEach((id) => result.set(id, []));

  const primaryByAccountId = new Map(
    accounts.map((a) => {
      const { contactName, contactEmail } = primaryContactFields(a);
      return [a.id, { contactName, contactEmail }];
    })
  );

  const docSeenByAccount = new Map();

  const ingestDoc = (docSnap) => {
    const data = docSnap.data();
    const aid = data.accountId || data.clientId;
    if (!aid || !result.has(aid)) return;

    const uid = docSnap.id;
    if (!docSeenByAccount.has(aid)) docSeenByAccount.set(aid, new Set());
    const seenIds = docSeenByAccount.get(aid);
    if (seenIds.has(uid)) return;

    const name = portalPersonDisplayName(data);
    const email = String(data.email || '').trim();
    if (!name && !email) return;

    const primary = primaryByAccountId.get(aid);
    const isPrimary = matchesPrimaryContact(name, email, primary?.contactName, primary?.contactEmail);

    seenIds.add(uid);
    result.get(aid).push({
      uid,
      name: name || email || 'Member',
      email,
      profileImage: data.profileImage || null,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phone: data.phone || data.phoneNumber || data.mobilePhone || '',
      isPrimary,
    });
  };

  // Community directory rows (getCommunityDirectory) already carry their logins: clients
  // can't query other companies' users docs (firestore.rules).
  if (accounts.every((a) => Array.isArray(a.portalUsers))) {
    accounts.forEach((a) => a.portalUsers.forEach((u) => ingestDoc({ id: u.id, data: () => u })));
  } else {
    for (let i = 0; i < ids.length; i += IN_CHUNK) {
      const chunk = ids.slice(i, i + IN_CHUNK);
      if (chunk.length === 0) continue;

      const usersRef = collection(db, 'users');
      const [snapA, snapC] = await Promise.all([
        getDocs(query(usersRef, where('accountId', 'in', chunk))),
        getDocs(query(usersRef, where('clientId', 'in', chunk))),
      ]);

      snapA.docs.forEach(ingestDoc);
      snapC.docs.forEach(ingestDoc);
    }
  }

  for (const aid of ids) {
    const arr = result.get(aid) || [];
    arr.sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }

  return result;
}

function alternateEmailsFromUserData(data) {
  return [
    data.communicationEmail,
    data.communication_email,
    data.workEmail,
    data.work_email,
    data.secondaryEmail,
    data.secondary_email,
    data.alternateEmail,
    data.alternate_email,
  ].filter((e) => e && typeof e === 'string');
}

function mapPortalUserToRosterMember(uid, data, isPrimary = false) {
  const firstName = data.firstName || '';
  const lastName = data.lastName || '';
  return {
    id: uid,
    firstName,
    lastName,
    email: String(data.email || '').trim(),
    alternateEmails: alternateEmailsFromUserData(data),
    name: portalPersonDisplayName(data) || String(data.email || '').trim() || 'Unknown',
    profileImage: data.profileImage || null,
    phone: data.phone || data.phoneNumber || data.mobilePhone || '',
    isPrimary: !!isPrimary,
  };
}

/**
 * Full client-side roster for CRM Events matching: portal users, CRM-linked user ids,
 * and pending portal invites for the account.
 */
export async function fetchClientEventRosterMembers(account) {
  if (!account?.id) return [];

  const membersById = new Map();
  const ingestUserDoc = (uid, data) => {
    if (!uid || !data) return;
    const { contactName, contactEmail } = primaryContactFields(account);
    const isPrimary = matchesPrimaryContact(
      portalPersonDisplayName(data),
      data.email,
      contactName,
      contactEmail
    );
    membersById.set(uid, mapPortalUserToRosterMember(uid, data, isPrimary));
  };

  const portalMap = await fetchPortalUsersLinkedToAccounts([account]);
  (portalMap.get(account.id) || []).forEach((u) => {
    ingestUserDoc(u.uid, {
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      profileImage: u.profileImage,
      displayName: u.name,
    });
    const existing = membersById.get(u.uid);
    if (existing) existing.isPrimary = !!u.isPrimary;
  });

  const extraUserIds = new Set([
    ...Object.keys(account.crmTeamMemberPhotos || {}),
    ...Object.keys(account.crmTeamMemberPhones || {}),
  ]);
  await Promise.all(
    [...extraUserIds].map(async (uid) => {
      if (membersById.has(uid)) return;
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (snap.exists()) ingestUserDoc(snap.id, snap.data());
      } catch {
        /* ignore missing docs */
      }
    })
  );

  try {
    const invitesSnap = await getDocs(
      query(collection(db, 'portalInvites'), where('leadId', '==', account.id))
    );

    // Emails that already have a real portal account — their invites are spent,
    // so listing them again would duplicate the person.
    const claimedEmails = new Set();
    membersById.forEach((m) => {
      if (m.email) claimedEmails.add(m.email.toLowerCase());
      (m.alternateEmails || []).forEach((e) =>
        claimedEmails.add(String(e).trim().toLowerCase())
      );
    });

    const now = new Date();
    const liveInviteByEmail = new Map();

    invitesSnap.docs.forEach((inviteDoc) => {
      const data = inviteDoc.data();
      const email = String(data.email || '').trim();
      if (!email) return;

      const emailKey = email.toLowerCase();
      if (claimedEmails.has(emailKey)) return;
      if ((data.status || 'pending') !== 'pending') return;

      const expiresAt =
        typeof data.expiresAt?.toDate === 'function'
          ? data.expiresAt.toDate()
          : data.expiresAt
          ? new Date(data.expiresAt)
          : null;
      if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt <= now) return;

      // Keep only the newest live invite per email so historical duplicates collapse
      const createdAt =
        typeof data.createdAt?.toDate === 'function'
          ? data.createdAt.toDate().getTime()
          : 0;
      const existing = liveInviteByEmail.get(emailKey);
      if (existing && existing.createdAt >= createdAt) return;
      liveInviteByEmail.set(emailKey, { id: inviteDoc.id, data, email, createdAt });
    });

    const { contactName, contactEmail } = primaryContactFields(account);
    liveInviteByEmail.forEach(({ id: inviteId, data, email }) => {
      const id = `invite:${inviteId}`;
      if (membersById.has(id)) return;
      membersById.set(id, {
        id,
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email,
        alternateEmails: [],
        name:
          [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
          email,
        profileImage: null,
        phone: '',
        isPrimary: matchesPrimaryContact(
          [data.firstName, data.lastName].filter(Boolean).join(' '),
          email,
          contactName,
          contactEmail
        ),
        isPendingInvite: true,
        inviteToken: data.token || '',
      });
    });
  } catch (err) {
    console.warn('Could not load portal invites for event roster:', err);
  }

  return Array.from(membersById.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}
